import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable } from 'rxjs';

import {
  EligiblePromotion,
  getEligiblePromotionProductId,
} from '@core/services/api/bogof/bogof-api.service';
import { IProductLabelWithMeta } from '@core/models/product.model';
import { selectEligiblePromotionsData } from '@store/eligible-promotions/eligible-promotions.selector';
import { SELECT_SETTINGS_PARAMETERS } from '@store/settings/settings.selection';
import { SettingsParametersState } from '@store/settings/settings.state';
import { modeMatchesPromotionAllowedModes } from '@shared/utils/promotion-distribution-mode/promotion-distribution-mode.util';

/**
 * Builds a map of productId -> promotion-derived labels (title from API).
 * Used to enrich shop products for display in product cards.
 */
@Injectable({ providedIn: 'root' })
export class EligiblePromotionsLabelsService {
  private readonly store = inject(Store);

  /**
   * Observable map of product id to promotion labels for that product.
   * Labels use the promotion title (e.g. "Big John à 5€", "1 menu enfant offert").
   */
  getPromotionLabelsByProductId$(): Observable<Map<string, IProductLabelWithMeta[]>> {
    return combineLatest([
      this.store.select(selectEligiblePromotionsData),
      this.store.select(SELECT_SETTINGS_PARAMETERS),
    ]).pipe(
      map(([promotions, parameters]) =>
        buildPromotionLabelsMap(promotions, resolveCurrentPromotionDistributionMode(parameters)),
      ),
    );
  }
}

function resolveCurrentPromotionDistributionMode(
  parameters: SettingsParametersState | undefined,
): string | null {
  const forcedMode = parameters?.force_distribution_mode?.trim();
  if (forcedMode) {
    return forcedMode;
  }

  const selectedMode = parameters?.distribution_mode?.trim();
  if (selectedMode) {
    return selectedMode;
  }

  return null;
}

export function buildPromotionLabelsMap(
  promotions: EligiblePromotion[] | null,
  currentDistributionMode: string | null,
): Map<string, IProductLabelWithMeta[]> {
  const map = new Map<string, IProductLabelWithMeta[]>();
  if (!promotions?.length) {
    return map;
  }

  promotions.forEach((promotion, index) => {
    // Backward compatibility: if API does not provide the flag, keep labels enabled.
    if (promotion.display_label_in_product_block === false) {
      return;
    }

    if (
      !modeMatchesPromotionAllowedModes(
        currentDistributionMode,
        promotion.allowed_distribution_modes,
      )
    ) {
      return;
    }

    const products = promotion.products ?? [];
    if (!products.length) {
      return;
    }

    products.forEach(p => {
      const productId = getEligiblePromotionProductId(p);
      const shopId = p.shop_id ?? '';
      if (!productId) {
        return;
      }

      const label: IProductLabelWithMeta = {
        id: `eligible-promo-${index}-${productId}`,
        handle: `eligible_promo_${index}_${productId}`,
        line_through: false,
        name: promotion.title,
        color: 'highlight',
        visible: true,
        filtereable: false,
        position: promotion.priority,
        entity_owner: { entity_id: shopId, entity_type: 'shop' },
        _meta: {
          origin: 'frontend',
          frontendType: 'promotion',
          type: promotion.type,
        },
      };

      const list = map.get(productId) ?? [];
      list.push(label);
      map.set(productId, list);
    });
  });

  return map;
}

/**
 * Merges promotion labels into each product's labels array (immutable).
 */
export function mergePromotionLabelsIntoProducts<
  T extends { id: string; labels?: IProductLabelWithMeta[] },
>(products: T[] | undefined, labelsByProductId: Map<string, IProductLabelWithMeta[]>): T[] {
  if (!products?.length) {
    return products ?? [];
  }
  return products.map(p => {
    const extraLabels = labelsByProductId.get(p.id) ?? [];
    if (extraLabels.length === 0) {
      return p;
    }
    return {
      ...p,
      labels: [...(p.labels ?? []), ...extraLabels],
    };
  });
}

/** Tree node shape used for category/product trees. */
export interface TreeCategoryWithProductLike {
  category: unknown;
  products: { id: string; labels?: IProductLabelWithMeta[] }[];
  children: TreeCategoryWithProductLike[];
  level?: number;
}

/**
 * Recursively merges promotion labels into every product in the tree (immutable).
 */
export function mergePromotionLabelsIntoTree<T extends TreeCategoryWithProductLike>(
  tree: T[],
  labelsByProductId: Map<string, IProductLabelWithMeta[]>,
): T[] {
  return tree.map(node => ({
    ...node,
    products: mergePromotionLabelsIntoProducts(node.products, labelsByProductId) as T['products'],
    children: mergePromotionLabelsIntoTree(node.children, labelsByProductId),
  })) as T[];
}
